import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartTooltip from "./ChartTooltip";
import ChartLegend from "./ChartLegend";
import { MAX_BAR_SIZE, axisProps, chrome } from "./chartTheme";

/**
 * Horizontal stacked bars - one row per question, one segment per response.
 *
 * Segments are separated by a 2px gap in the surface colour. Interior segments
 * only get an inline label when the number comfortably fits; anything narrower
 * is left to the legend, the tooltip and the table view rather than being
 * clipped.
 *
 * @param {{name: string, [seriesKey]: number}[]} data
 * @param {{key: string, label: string, color: string}[]} series in fixed order
 */
export default function StackedBarChart({
  data = [],
  series = [],
  height = 260,
  categoryWidth = 130,
  minLabelWidth = 28,
  barSize = MAX_BAR_SIZE,
}) {
  const theme = chrome();
  const axis = axisProps(theme);

  /* Only draw the value inside a segment wide enough to hold it. */
  const renderInlineLabel = (props) => {
    const { x, y, width, height: h, value } = props;
    if (!value || width < minLabelWidth) return null;
    return (
      <text
        x={x + width / 2}
        y={y + h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill={theme.surface}
        fontSize={11}
        fontWeight={700}
      >
        {value}
      </text>
    );
  };

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
          barCategoryGap="30%"
        >
          <CartesianGrid stroke={theme.grid} horizontal={false} />
          <XAxis type="number" {...axis} />
          <YAxis type="category" dataKey="name" width={categoryWidth} {...axis} />
          <Tooltip
            cursor={{ fill: theme.grid, fillOpacity: 0.5 }}
            content={<ChartTooltip />}
          />

          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="responses"
              fill={s.color}
              maxBarSize={barSize}
              /* 2px of surface between touching segments. */
              stroke={theme.surface}
              strokeWidth={2}
              radius={i === series.length - 1 ? [0, 4, 4, 0] : 0}
              isAnimationActive={false}
            >
              <LabelList dataKey={s.key} content={renderInlineLabel} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>

      <ChartLegend
        items={series.map((s) => ({ name: s.label, color: s.color }))}
      />
    </div>
  );
}
